/**
 * UUID 및 기본 데이터 검증 유틸리티
 * Framework-independent pure validation functions
 */

/**
 * UUID v4 검증
 * 개발/테스트 환경과 프로덕션 환경의 균형잡힌 검증
 *
 * @example
 * isValidUUID('123e4567-e89b-12d3-a456-426614174000') // true
 * isValidUUID('test-123') // true (개발/테스트 환경)
 * isValidUUID('invalid') // false
 */
export function isValidUUID(uuid: string): boolean {
  // 빈 문자열이나 null 체크
  if (!uuid || typeof uuid !== 'string') {
    return false;
  }

  // 공백 제거 및 정규화
  const normalizedUuid = uuid.trim();

  // 길이 체크 (최소 8자, 최대 36자)
  if (normalizedUuid.length < 8 || normalizedUuid.length > 36) {
    return false;
  }

  // 1. 표준 UUID v4 형식 (프로덕션 기본)
  const standardUuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (standardUuidRegex.test(normalizedUuid)) {
    return true;
  }

  // 2. 테스트/개발 환경에서만 허용되는 추가 형식
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalEnv = (globalThis as any).process?.env?.NODE_ENV;
  const isDevOrTest = globalEnv === 'test' || globalEnv === 'development';

  if (isDevOrTest) {
    // nil UUID (모든 환경에서 유효)
    if (normalizedUuid === '00000000-0000-0000-0000-000000000000') {
      return true;
    }

    // 테스트용 간단한 ID 패턴 (특정 프리픽스로 시작하는 경우에만 허용)
    const testPrefixes = [
      'test-',
      'user-',
      'mock-',
      'demo-',
      'fixture-',
      'sender-',
      'room-',
      'channel-',
      'quote-',
    ];
    const hasValidTestPrefix = testPrefixes.some((prefix) =>
      normalizedUuid.startsWith(prefix)
    );

    if (
      hasValidTestPrefix &&
      /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(normalizedUuid) &&
      normalizedUuid.length >= 8 &&
      normalizedUuid.length <= 24 // 너무 긴 경우 제외
    ) {
      // 하이픈으로만 구성되거나 연속된 하이픈 방지
      if (!/^-+$/.test(normalizedUuid) && !/-{2,}/.test(normalizedUuid)) {
        return true;
      }
    }

    // 16진수 형식 (하이픈 없는 UUID)
    if (/^[0-9a-f]{32}$/i.test(normalizedUuid)) {
      return true;
    }
  }

  return false;
}

/**
 * 사용자 유형 검증 (User Type Guards)
 */

export function isBuyer(userType: string | null | undefined): boolean {
  return String(userType).toLowerCase() === 'buyer';
}

export function isSupplier(userType: string | null | undefined): boolean {
  return String(userType).toLowerCase() === 'supplier';
}

export function isAdmin(userType: string | null | undefined): boolean {
  return String(userType).toLowerCase() === 'admin';
}

/**
 * 이메일 검증
 *
 * @example
 * isValidEmail('user@example.com') // true
 * isValidEmail('invalid') // false
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * 문자열 유효성 검증 (빈 문자열 제외)
 *
 * @example
 * isValidString('hello') // true
 * isValidString('  ') // false
 * isValidString(null) // false
 */
export function isValidString(str: string | null | undefined): boolean {
  return (
    str !== null &&
    str !== undefined &&
    typeof str === 'string' &&
    str.trim().length > 0
  );
}

/**
 * 한국 휴대폰 번호 검증
 *
 * @example
 * isValidMobile('010-1234-5678') // true
 * isValidMobile('01012345678') // true
 * isValidMobile('011-1234-5678') // false (더 이상 사용 안함)
 */
export function isValidMobile(phone: string): boolean {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  const numbers = phone.replace(/\D/g, '');

  // 010으로 시작하지만 11자리가 아닌 경우
  if (numbers.startsWith('010') && numbers.length !== 11) {
    return false;
  }

  // 휴대폰 번호 (11자리만 유효, 010만 현재 사용)
  if (numbers.length === 11 && numbers.startsWith('010')) {
    return true;
  }

  return false;
}

/**
 * 한국 전화번호 검증 (휴대폰 + 지역번호 + 070)
 *
 * @example
 * isValidKoreanPhone('010-1234-5678') // true (휴대폰)
 * isValidKoreanPhone('02-1234-5678') // true (서울)
 * isValidKoreanPhone('031-123-4567') // true (경기)
 * isValidKoreanPhone('070-1234-5678') // true (인터넷전화)
 */
export function isValidKoreanPhone(phone: string): boolean {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  const numbers = phone.replace(/\D/g, '');

  // 1. 휴대폰 번호 (010만 현재 사용, 11자리)
  if (numbers.startsWith('010') && numbers.length === 11) {
    return true;
  }

  // 2. 서울 지역번호 (02, 9~10자리)
  if (numbers.startsWith('02') && (numbers.length === 9 || numbers.length === 10)) {
    return true;
  }

  // 3. 기타 지역번호 및 070, 080 (3자리 시작, 10~11자리)
  const validPrefixes = [
    '031', '032', '033', // 수도권/강원
    '041', '042', '043', '044', // 충청/세종
    '051', '052', '053', '054', '055', // 경상
    '061', '062', '063', '064', // 전라/제주
    '070', '080' // 인터넷/수신자부담
  ];

  const prefix = numbers.substring(0, 3);
  if (validPrefixes.includes(prefix) && (numbers.length === 10 || numbers.length === 11)) {
    return true;
  }

  return false;
}

/**
 * Luhn (mod-10) 신용카드 번호 체크섬 검증
 * 프레임워크 독립 순수 함수 — backend/frontend 공통 사용
 *
 * @example
 * isValidLuhn('4242424242424242') // true (Visa test card)
 * isValidLuhn('1234567890123456') // false
 * isValidLuhn('4242-4242-4242-4242') // false (숫자 이외 문자 포함)
 */
export function isValidLuhn(value: string): boolean {
  if (!/^\d+$/.test(value)) return false;
  let sum = 0;
  let alternate = false;
  for (let i = value.length - 1; i >= 0; i--) {
    let n = Number(value.charAt(i));
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}
